import type React from "react";
import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { AppointmentV2 } from "api/services/appointment/schemas/appointment_v2";
import type { BookingIntervalResponse } from "api/services/appointment/schemas/booking_interval";
import type { FormStates } from "routes/calendar/helpers/misc";
import { BaseButton } from "shared/atoms/BaseButton";
import { Notification } from "shared/atoms/Notification";
import { Spinner } from "shared/atoms/Spinner";

type Translations = {
  add: string;
  delete: string;
  confirmDeletion: string;
  success: string;
  successDeletion: string;
  successEdit: string;
  update: string;
};

interface Props {
  formState: FormStates;
  error: string | null;
  editMode: boolean;
  event?: BookingIntervalResponse | AppointmentV2 | null;
  deleteAppointment: () => void;
  text?: Partial<Translations>;
  disabled?: boolean;
}

export const FormFooter: React.FC<Props> = ({
  formState,
  error,
  editMode,
  event,
  deleteAppointment,
  text = {},
  disabled,
}) => {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deletion, setDeletion] = useState(false);

  const translations: Translations = {
    add: t("booking.forms.book_appointment"),
    delete: t("booking.buttons.cancel"),
    confirmDeletion: t("booking.form.confirm_deletion"),
    success: t("booking.success"),
    successEdit: t("booking.success_edit"),
    successDeletion: t("booking.success_deletion"),
    update: t("booking.buttons.update_appointment"),
    ...text,
  };

  useEffect(() => {
    setDeletion(false);
  }, [event]);

  if (formState === "saving" || formState === "success") {
    return (
      <FooterWrapper>
        {formState === "saving" && <Spinner style={{ width: 50, height: 50 }} />}
        {formState === "success" && (
          <Notification variant="success">
            {deletion && translations.successDeletion}
            {!deletion && (editMode ? translations.successEdit : translations.success)}
          </Notification>
        )}
      </FooterWrapper>
    );
  }

  return (
    <>
      {error && (
        <Notification variant="danger" style={{ margin: "20px 0 20px 0" }}>
          {error}
        </Notification>
      )}

      <FooterWrapper>
        {confirmDelete && (
          <Buttons>
            <Notification variant="warning" style={{ margin: "0 0 20px 0" }}>
              {translations.confirmDeletion}
            </Notification>
            <BaseButton
              text={t("buttons.confirm")}
              dataTestID="confirm-delete-appointment"
              variant="danger"
              onClick={() => {
                deleteAppointment();
                setDeletion(true);
              }}
              size="large"
              fullWidth
            />
            <BaseButton
              text={t("buttons.cancel")}
              dataTestID="cancel-delete"
              variant="tertiaryBlue"
              onClick={() => setConfirmDelete(false)}
              size="large"
              fullWidth
            />
          </Buttons>
        )}

        {!confirmDelete && (
          <Buttons>
            <BaseButton
              dataTestID="save-booking-button"
              text={editMode ? translations.update : translations.add}
              type="submit"
              size="large"
              fullWidth
              disabled={disabled}
            />

            {editMode && event && (
              <BaseButton
                text={translations.delete}
                variant="danger"
                onClick={() => setConfirmDelete(true)}
                size="large"
                fullWidth
              />
            )}
          </Buttons>
        )}
      </FooterWrapper>
    </>
  );
};

const FooterWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-bottom: 20px;
`;

const Buttons = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 8px;
  gap: 16px;
`;
